// TopNav — Phase-1 nav redesign: the primary navigation as a horizontal
// icon-above-label bar (replaces the former left sidebar). Purely presentational:
// it receives an ALREADY-FILTERED, ALREADY-GROUPED node list from AppShell and
// never makes an access decision — the roles filter + mode overrides + the
// pure-account-manager force all still live in AppShell. A "group" node (e.g.
// Fax → Inbox / Send) only ever carries the children AppShell already allowed.
//
// Narrow screens: nodes that don't fit collapse into a keyboard-reachable "More"
// menu (measured via ResizeObserver + a hidden mirror). When the active section
// is the one that overflowed, the trigger shows THAT section so "you are here"
// is never lost.

import React, { useRef, useState, useLayoutEffect, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MoreHorizontal, ChevronDown } from 'lucide-react';

type IconType = React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;

export interface TopNavChild {
  path: string;
  icon: IconType;
  label: string;
}
export interface TopNavLeaf {
  kind: 'leaf';
  path: string;
  icon: IconType;
  label: string;
}
export interface TopNavGroup {
  kind: 'group';
  key: string;
  icon: IconType;
  label: string;
  children: TopNavChild[];
}
export type TopNavNode = TopNavLeaf | TopNavGroup;

interface TopNavProps {
  items: TopNavNode[];
  pathname: string;
  onPrefetch: (path: string) => void;
}

// Exact active rule preserved from the old sidebar: exact match OR a true
// sub-route (path + '/'), so a shorter path (/fax) never lights up for a
// sibling (/fax-insights) while a parent (/admin) still highlights on its
// sub-routes (/admin/fax).
function isActivePath(pathname: string, path: string) {
  return pathname === path || pathname.startsWith(path + '/');
}
function nodeActive(node: TopNavNode, pathname: string) {
  return node.kind === 'leaf'
    ? isActivePath(pathname, node.path)
    : node.children.some((c) => isActivePath(pathname, c.path));
}

// Focus ring is drawn INSET so it sits cleanly within the compact pill.
const FOCUS_RING = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#C94B1F]';

const itemClass = (active: boolean) =>
  `flex flex-col items-center justify-center gap-0.5 px-2.5 py-1.5 rounded-lg transition-colors flex-shrink-0 ${FOCUS_RING} ${
    active
      ? 'bg-[#C94B1F]/10 text-[#C94B1F] font-semibold'
      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
  }`;

// A pill's label row; group pills append a small caret to signal the dropdown.
function PillLabel({ label, caret, open }: { label: string; caret?: boolean; open?: boolean }) {
  return (
    <span className="text-[11px] leading-none tracking-tight whitespace-nowrap flex items-center gap-0.5">
      {label}
      {caret && <ChevronDown className={`w-2.5 h-2.5 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />}
    </span>
  );
}

// A child link rendered inside a dropdown (group menu or the More menu).
function ChildLink({
  child, pathname, onPrefetch, onNavigate,
}: { child: TopNavChild; pathname: string; onPrefetch: (p: string) => void; onNavigate: () => void }) {
  const active = isActivePath(pathname, child.path);
  return (
    <Link
      to={child.path}
      onMouseEnter={() => onPrefetch(child.path)}
      onClick={onNavigate}
      className={`flex items-center gap-3 px-3 py-2 text-sm transition-colors ${FOCUS_RING} ${
        active ? 'bg-[#C94B1F]/10 text-[#C94B1F] font-semibold' : 'text-gray-700 hover:bg-gray-50'
      }`}
      aria-current={active ? 'page' : undefined}
    >
      <child.icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
      <span className="truncate">{child.label}</span>
    </Link>
  );
}

export default function TopNav({ items, pathname, onPrefetch }: TopNavProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const measurerRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const moreBtnRef = useRef<HTMLButtonElement>(null);
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const groupBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [moreOpen, setMoreOpen] = useState(false);
  const [openGroupKey, setOpenGroupKey] = useState<string | null>(null);

  // Re-run measurement when the node set changes (paths/labels drive width).
  const itemsKey = items
    .map((n) => (n.kind === 'leaf' ? n.path : `${n.key}(${n.children.map((c) => c.path).join(',')})`))
    .join('|');

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measurer = measurerRef.current;
    if (!row || !measurer) return;

    const compute = () => {
      const n = items.length;
      const kids = Array.from(measurer.children) as HTMLElement[];
      if (kids.length <= 1 || n === 0) {
        setVisibleCount(n);
        return;
      }
      // Small buffer so a font-medium ACTIVE pill (which the inactive-weight
      // mirror under-measures by a few px) can't clip at a boundary width.
      const BUFFER = 8;
      const avail = row.clientWidth - BUFFER;
      const base = measurer.getBoundingClientRect().left;
      const moreW = kids[kids.length - 1].getBoundingClientRect().width;
      const rights = kids.slice(0, n).map((el) => el.getBoundingClientRect().right - base);

      if (rights[n - 1] <= avail) {
        setVisibleCount(n);
        return;
      }
      const GAP = 4; // gap-1 between the last visible item and the More button
      let count = 0;
      for (let i = 0; i < n; i++) {
        if (rights[i] + GAP + moreW <= avail) count = i + 1;
        else break;
      }
      setVisibleCount(count);
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(row);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey]);

  // Close the More menu on outside click / Escape (focus-out handled by onBlur).
  useEffect(() => {
    if (!moreOpen) return;
    const onDown = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMoreOpen(false);
        moreBtnRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);

  // Close an open group menu on outside click / Escape.
  useEffect(() => {
    if (!openGroupKey) return;
    const onDown = (e: MouseEvent) => {
      const wrap = groupRefs.current[openGroupKey];
      if (wrap && !wrap.contains(e.target as Node)) setOpenGroupKey(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenGroupKey(null);
        groupBtnRefs.current[openGroupKey]?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openGroupKey]);

  const safeCount = Math.min(visibleCount, items.length);
  const visible = items.slice(0, safeCount);
  const overflow = items.slice(safeCount);
  const activeOverflow = overflow.find((n) => nodeActive(n, pathname));

  // If a widen collapses the overflow away, don't leave a now-orphaned menu open.
  useEffect(() => {
    if (overflow.length === 0 && moreOpen) setMoreOpen(false);
  }, [overflow.length, moreOpen]);

  // If the open group scrolled into overflow (its pill unmounts, killing its
  // outside-click/Escape/blur handlers), clear the stale open state so it can't
  // re-pop with no user action when the pill remounts on a widen.
  const openGroupVisible = openGroupKey
    ? visible.some((n) => n.kind === 'group' && n.key === openGroupKey)
    : true;
  useEffect(() => {
    if (!openGroupVisible) setOpenGroupKey(null);
  }, [openGroupVisible]);

  const renderLeaf = (leaf: TopNavLeaf) => {
    const active = isActivePath(pathname, leaf.path);
    return (
      <Link
        key={leaf.path}
        to={leaf.path}
        onMouseEnter={() => onPrefetch(leaf.path)}
        className={itemClass(active)}
        aria-current={active ? 'page' : undefined}
      >
        <leaf.icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
        <PillLabel label={leaf.label} />
      </Link>
    );
  };

  const renderGroup = (group: TopNavGroup) => {
    const active = nodeActive(group, pathname);
    const open = openGroupKey === group.key;
    return (
      <div
        key={group.key}
        ref={(el) => { groupRefs.current[group.key] = el; }}
        className="relative flex-shrink-0"
        onBlur={(e) => {
          const w = groupRefs.current[group.key];
          if (w && !w.contains(e.relatedTarget as Node)) setOpenGroupKey((k) => (k === group.key ? null : k));
        }}
      >
        <button
          ref={(el) => { groupBtnRefs.current[group.key] = el; }}
          type="button"
          onClick={() => setOpenGroupKey((k) => (k === group.key ? null : group.key))}
          aria-expanded={open}
          aria-current={active ? 'page' : undefined}
          className={`flex flex-col items-center justify-center gap-0.5 px-2.5 py-1.5 rounded-lg transition-colors flex-shrink-0 ${FOCUS_RING} ${
            active
              ? 'bg-[#C94B1F]/10 text-[#C94B1F] font-semibold'
              : open
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
          }`}
        >
          <group.icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
          <PillLabel label={group.label} caret open={open} />
        </button>
        {open && (
          <div className="absolute left-0 top-full mt-2 w-48 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 py-1">
            {group.children.map((c) => (
              <ChildLink key={c.path} child={c} pathname={pathname} onPrefetch={onPrefetch} onNavigate={() => setOpenGroupKey(null)} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div ref={rowRef} className="relative flex-1 min-w-[3rem] flex items-center gap-1">
      {visible.map((node) => (node.kind === 'leaf' ? renderLeaf(node) : renderGroup(node)))}

      {overflow.length > 0 && (
        <div
          ref={moreRef}
          className="relative flex-shrink-0"
          onBlur={(e) => {
            if (moreRef.current && !moreRef.current.contains(e.relatedTarget as Node)) setMoreOpen(false);
          }}
        >
          <button
            ref={moreBtnRef}
            type="button"
            onClick={() => setMoreOpen((o) => !o)}
            aria-expanded={moreOpen}
            aria-current={activeOverflow ? 'page' : undefined}
            aria-label={activeOverflow ? `More — ${activeOverflow.label} (current section)` : 'More sections'}
            className={`flex flex-col items-center justify-center gap-0.5 px-2.5 py-1.5 rounded-lg transition-colors flex-shrink-0 ${FOCUS_RING} ${
              activeOverflow
                ? 'bg-[#C94B1F]/10 text-[#C94B1F] font-semibold'
                : moreOpen
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
            }`}
          >
            {activeOverflow ? (
              <activeOverflow.icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
            ) : (
              <MoreHorizontal className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
            )}
            <PillLabel label={activeOverflow ? activeOverflow.label : 'More'} />
          </button>
          {moreOpen && (
            <div className="absolute left-0 top-full mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 py-1">
              {overflow.map((node) =>
                node.kind === 'leaf' ? (
                  <ChildLink key={node.path} child={node} pathname={pathname} onPrefetch={onPrefetch} onNavigate={() => setMoreOpen(false)} />
                ) : (
                  <div key={node.key} className="py-0.5" role="group" aria-label={node.label}>
                    <div className="px-3 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{node.label}</div>
                    {node.children.map((c) => (
                      <ChildLink key={c.path} child={c} pathname={pathname} onPrefetch={onPrefetch} onNavigate={() => setMoreOpen(false)} />
                    ))}
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}

      {/* Hidden mirror — one pill per node (+ a sample More button) off-screen
          so the measurement above can size nodes even while they're collapsed. */}
      <div
        ref={measurerRef}
        aria-hidden
        className="absolute -top-[9999px] left-0 flex items-center gap-1 pointer-events-none opacity-0"
      >
        {items.map((node) => (
          <span key={node.kind === 'leaf' ? node.path : node.key} className={itemClass(false)}>
            <node.icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
            <PillLabel label={node.label} caret={node.kind === 'group'} />
          </span>
        ))}
        <span className="flex flex-col items-center justify-center gap-0.5 px-2.5 py-1.5 rounded-lg flex-shrink-0">
          <MoreHorizontal className="w-5 h-5" aria-hidden="true" />
          <PillLabel label="More" />
        </span>
      </div>
    </div>
  );
}
